/*------------------------------------*\
    MODULES
\*------------------------------------*/
import merge from 'lodash/merge';

/*------------------------------------*\
    DISPLAY TOGGLE MODULE
\*------------------------------------*/
class DisplayToggle {

    constructor(passedElement, passedSettings = {}) {
        let self = this;

        const coreSettings = {
            triggers: {
                selector: '.js-display-toggle__trigger',
                panelSelector: 'data-panel-id',
                items: [], // Store DOM collection as array
                activeState: {
                    attribute: 'aria-expanded',
                    value: 'true'
                },
                inactiveState: {
                    attribute: 'aria-expanded',
                    value: 'false'
                },
                autoShow: {
                    attribute: 'data-auto-show',
                    value: 'true',
                    isEnabled: true
                },
                boundStatusClass: 'js-display-toggle__is-event-bound'
            },
            panels: {
                selector: '.js-display-toggle__panel',
                items: [], // Store DOM collection as array
                activeState: {
                    attribute: 'aria-hidden',
                    value: 'false'
                },
                inactiveState: {
                    attribute: 'aria-hidden',
                    value: 'true'
                }
            },
            closeAllOnEscape: false
        };

        // Set the passed elem
        self.elem = passedElement;

        // Merge the two settings objects to create the global settings
        self.settings = merge({}, coreSettings, passedSettings);

        // Load up the panels and triggers
        self.settings.triggers.items = [...self.elem.querySelectorAll(self.settings.triggers.selector)];
        self.settings.panels.items = [...self.elem.querySelectorAll(self.settings.panels.selector)];
    }

    // Init method called externally on instantiation
    init() {
        let self = this;

        // Run the after init method if exists
        if(typeof(self.afterInit) === 'function') {
            self.afterInit();
            self.bindEvents();
            self.autoShow();
        }
        else {
            self.bindEvents();
            self.autoShow();
        }
    }

    // Bind any interactive events
    bindEvents() {
        let self = this;

        self.settings.triggers.items.map(trigger => {

            if(!trigger.classList.contains(self.settings.triggers.boundStatusClass)) {

                // Attach click
                trigger.addEventListener('click', evt => { self.triggerEventHandler(evt, trigger) });

                // Attach enter keypress
                trigger.addEventListener('keypress', evt => { if(evt.keyCode === 13) self.triggerEventHandler(evt, trigger); });

                // Add the bound status class
                trigger.classList.add(self.settings.triggers.boundStatusClass);
            }
        });

        // If closing all on escape is enabled, bind it up
        if(self.settings.closeAllOnEscape) {

            document.addEventListener('keyup', (evt) => {

                // Loop and hide everything if it was the escape key that was pressed
                if(evt.keyCode === 27) {
                    self.settings.triggers.items.map(item => { self.toggle('hide', item) });
                }
            });
        }
    }

    // A global handler for trigger events
    triggerEventHandler(evt, trigger) {
        let self = this;

        evt.preventDefault();

        // Run toggle based on state
        if(trigger.getAttribute(self.settings.triggers.activeState.attribute) === self.settings.triggers.activeState.value) {
            self.toggle('hide', trigger);
        }
        else {
            self.toggle('show', trigger);
        }
    }

    // The main toggle method
    toggle(command, trigger) {
        let self = this;
        let panel = self.findPanel(trigger);
        let relatedTriggers = self.findTriggerSiblings(trigger);

        // Bail out if no panel found
        if(!panel) {
            return;
        }

        // Show or hide base on command
        switch(command) {
            case 'show':
            default:

                relatedTriggers.map(item => item.setAttribute(self.settings.triggers.activeState.attribute, self.settings.triggers.activeState.value));
                panel.setAttribute(self.settings.panels.activeState.attribute, self.settings.panels.activeState.value);
                break;

            case 'hide':

                relatedTriggers.map(item => item.setAttribute(self.settings.triggers.inactiveState.attribute, self.settings.triggers.inactiveState.value));
                panel.setAttribute(self.settings.panels.inactiveState.attribute, self.settings.panels.inactiveState.value);
                break;
        }

        // Look for an afterToggle function and run it if it exists
        if(typeof(self.afterToggle) === 'function') {
            self.afterToggle(command, trigger);
        }
    }

    // Attempt to link a panel to a trigger
    findPanel(trigger) {
        let self = this;
        let queryAttribute = self.getTriggerQueryAttribute(trigger);

        return self.settings.panels.items.filter(panel => panel.getAttribute('id') === queryAttribute)[0];
    }

    // Return either the href or defined trigger panel querying attribute
    getTriggerQueryAttribute(trigger) {
        let self = this;

        // Send the generic selector if no trigger passed
        if(typeof(trigger) === 'undefined') return self.settings.triggers.panelSelector;

        return (trigger.hasAttribute('href') ? trigger.getAttribute('href') : trigger.getAttribute(self.settings.triggers.panelSelector)).replace('#', '').trim();
    }

    // Return the passed trigger with its siblings
    findTriggerSiblings(trigger) {
        let self = this;
        let queryAttribute = self.getTriggerQueryAttribute(trigger);

        return self.settings.triggers.items.filter(item => {
            return self.getTriggerQueryAttribute(item) === queryAttribute;
        })
    }

    // Look for items that need to be shown by default
    autoShow() {
        let self = this;

        // if not enabled, bail
        if(!self.settings.triggers.autoShow.isEnabled) return;

        setTimeout(function() {
            self.settings.triggers.items.map(item => {
                // If the item needs to be shown, run the toggle
                if(item.getAttribute(self.settings.triggers.autoShow.attribute) === self.settings.triggers.autoShow.value) {

                    self.toggle('show', item);
                }
            });
        }, 500);
    }
}

export default DisplayToggle;
